/**
 * Base Service Utilities
 * Provides standardized patterns for service layer
 */

/**
 * Standard error handler for service methods
 * Logs error and re-throws with consistent format
 * @param {string} context - Description of the operation
 * @param {Error} error - The caught error
 * @throws {Error} Formatted error
 */
function handleServiceError(context, error) {
  console.error(`[Service Error] ${context}:`, error);
  throw new Error(error.message || `Failed to ${context.toLowerCase()}`);
}

/**
 * Standard error handler that returns a default value instead of throwing
 * Use for non-critical operations where a fallback is acceptable
 * @param {string} context - Description of the operation
 * @param {Error} error - The caught error
 * @param {any} defaultValue - Value to return on error
 * @returns {any} The default value
 */
function handleServiceErrorWithDefault(context, error, defaultValue) {
  console.error(`[Service Error] ${context}:`, error);
  return defaultValue;
}

/**
 * Wrapper to execute service function with standard error handling
 * @param {Function} fn - Async function to execute
 * @param {string} context - Description for error logging
 * @returns {Promise<any>} Result of the function
 */
export async function withErrorHandling(fn, context) {
  try {
    return await fn();
  } catch (error) {
    handleServiceError(context, error);
  }
}

/**
 * Wrapper to execute service function with fallback on error
 * @param {Function} fn - Async function to execute
 * @param {string} context - Description for error logging
 * @param {any} fallback - Value to return on error
 * @returns {Promise<any>} Result of the function or fallback
 */
export async function withFallback(fn, context, fallback) {
  try {
    return await fn();
  } catch (error) {
    return handleServiceErrorWithDefault(context, error, fallback);
  }
}

/**
 * Extract data from standard API response
 * Handles common response patterns: { items: [] }, { data: [] }, { [key]: [] }
 * @param {Object} response - API response object
 * @param {string} key - Expected key for the data array
 * @returns {Array} Extracted array or empty array
 */
export function extractResponseData(response, key) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response[key])) return response[key];

  // `{ success, data: { templates: [...] } }` — the shape most D1 routes
  // answer with. `response.data` is an OBJECT there, and handing it back is
  // how the booking-templates modal came to call `.map` on one and take the
  // whole dialog down. This says what it promises: an array, or none.
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray(data[key])) return data[key];

  if (Array.isArray(response.items)) return response.items;

  // Anything else — a paginated envelope like `{ data: { items, total } }`, or
  // a shape nobody has met yet — deliberately yields an empty array rather
  // than the object itself. The next paginated endpoint should pass its own
  // key (`extractResponseData(res, 'items')`) or unwrap before calling, not
  // rely on this guessing: a caller that maps over the result must never be
  // handed something that has no `.map`.
  return [];
}

/**
 * Extract single item from API response
 * @param {Object} response - API response object
 * @param {string} key - Expected key for the item
 * @returns {Object|null} Extracted item or null
 */
export function extractResponseItem(response, key) {
  if (!response) return null;
  if (response[key]) return response[key];
  if (response.data) return response.data;
  if (response.item) return response.item;
  return null;
}

/**
 * Parse a boolean out of a system_settings value.
 *
 * `system_settings.value` is a TEXT column and has been written by three
 * different hands: the master-admin UI writes 'true'/'false', the seed and
 * migration SQL writes '1'/'0', and a couple of rows were typed in by hand.
 * Reading only 'true' is how production's `enable_kits` = '1' was read as OFF
 * and the kits stayed hidden on a system that had switched them on.
 *
 * So every spelling of "on" this database actually contains counts as on, and
 * everything else — including a missing row, which arrives here as undefined —
 * counts as off.
 * @param {any} value - Value to parse
 * @returns {boolean}
 */
export function parseSettingBoolean(value) {
  if (value === true) return true;
  if (typeof value === 'number') return value === 1;
  if (typeof value !== 'string') return false;
  return ['true', '1', 'yes', 'on'].includes(value.trim().toLowerCase());
}
